import {
  addDays,
  daysBetween,
  PLANNING_KIND_LABELS,
  PLANNING_STATUSES,
  planningDay,
  planningMoments,
  planningStatus,
  type PlanningAppointmentDto,
  type PlanningKind,
  type PlanningLineDto,
  type PlanningMoment,
  type PlanningStatus,
} from "@klantpunt/shared";

/** Regel met berekende status en momenten in de getoonde periode. */
export interface PlanRow {
  l: PlanningLineDto;
  status: PlanningStatus;
  moments: PlanningMoment[];
  /** Iets te doen in de komende vier weken (vervaldatum of prognose). */
  soon: boolean;
}

export type Group = "gebouw" | "uitvoerder" | "soort";
export type StatusFilter = "" | "verlopen" | "na_vervaldatum" | "te_plannen" | "binnenkort";

export interface PlanFilters {
  q: string;
  buildingId: string;
  /** Naam van de uitvoerder; "-" = zonder uitvoerder. */
  performer: string;
  kind: PlanningKind | "";
}
export const NO_FILTERS: PlanFilters = { q: "", buildingId: "", performer: "", kind: "" };
export const activeFilterCount = (f: PlanFilters) => [f.buildingId, f.performer, f.kind].filter(Boolean).length;

const NO_PERFORMER = "-";
export const performerKey = (l: PlanningLineDto) => l.performer?.name || NO_PERFORMER;

export function toRows(lines: PlanningLineDto[], from: string, to: string, today: string): PlanRow[] {
  const in4 = addDays(today, 27);
  return lines.map((l) => {
    const status = planningStatus(l, today);
    return {
      l,
      status,
      moments: planningMoments(l, from, to),
      soon:
        status !== "verlopen" && status !== "klaar" && planningMoments(l, today, in4).some((m) => m.type !== "done"),
    };
  });
}

/** Zoeken, gebouw, uitvoerder en soort (de tellers volgen dit). */
export function applyFilters(rows: PlanRow[], f: PlanFilters, ofClient: (buildingId: string) => boolean) {
  const q = f.q.trim().toLowerCase();
  return rows.filter(({ l }) => {
    if (!ofClient(l.building.id)) return false;
    if (f.buildingId && l.building.id !== f.buildingId) return false;
    if (f.performer && performerKey(l) !== f.performer) return false;
    if (f.kind && l.kind !== f.kind) return false;
    if (!q) return true;
    return [l.title, l.code, l.asset?.code, l.asset?.name, l.building.code, l.building.name, l.performer?.name]
      .filter(Boolean)
      .some((s) => s!.toLowerCase().includes(q));
  });
}

export const counts = (rows: PlanRow[]) => ({
  verlopen: rows.filter((r) => r.status === "verlopen").length,
  na_vervaldatum: rows.filter((r) => r.status === "na_vervaldatum").length,
  te_plannen: rows.filter((r) => r.status === "te_plannen").length,
  binnenkort: rows.filter((r) => r.soon).length,
});

/** In beeld: iets in de periode, of verlopen (moet nog steeds gebeuren). */
export function visibleRows(rows: PlanRow[], status: StatusFilter) {
  return rows.filter((r) => {
    if (status === "binnenkort") return r.soon;
    if (status) return r.status === status;
    return r.moments.length > 0 || r.status === "verlopen" || r.status === "na_vervaldatum";
  });
}

const statusRank = (s: PlanningStatus) => PLANNING_STATUSES.indexOf(s);

export interface RowGroup {
  key: string;
  title: string;
  sub: string | null;
  rows: PlanRow[];
}

export function groupRows(rows: PlanRow[], by: Group): RowGroup[] {
  const m = new Map<string, RowGroup>();
  for (const r of rows) {
    const { l } = r;
    const g =
      by === "gebouw"
        ? { key: l.building.id, title: `${l.building.code} ${l.building.name}`, sub: l.clientName }
        : by === "uitvoerder"
          ? {
              key: performerKey(l),
              title: l.performer?.name || "Zonder uitvoerder",
              sub: null,
            }
          : { key: l.kind, title: PLANNING_KIND_LABELS[l.kind], sub: null };
    const cur = m.get(g.key) ?? { ...g, rows: [] };
    cur.rows.push(r);
    m.set(g.key, cur);
  }
  const order = (g: RowGroup) =>
    by === "soort"
      ? String(Object.keys(PLANNING_KIND_LABELS).indexOf(g.key)).padStart(2, "0")
      : g.key === NO_PERFORMER
        ? "￿"
        : g.title.toLowerCase();
  const out = [...m.values()].sort((a, b) => order(a).localeCompare(order(b), "nl"));
  for (const g of out)
    g.rows.sort(
      (a, b) =>
        statusRank(a.status) - statusRank(b.status) ||
        (a.l.dueOn ?? "9999").localeCompare(b.l.dueOn ?? "9999") ||
        a.l.title.localeCompare(b.l.title, "nl"),
    );
  return out;
}

/** Waar een regel vandaan komt: de asset (uitvoering vastleggen), de taak of de meterronde. */
export function lineHref(l: PlanningLineDto) {
  if (l.source === "meter") return `/meterstanden/ronde/${l.refId}`;
  if (l.source === "task") return `/gebouwen/${l.building.id}/taken?taak=${l.refId}`;
  return `/gebouwen/${l.building.id}/keuringen?${l.asset ? `asset=${l.asset.id}` : "gebouw=1"}`;
}

/** Wat er in een cel staat: hooguit één teken per soort moment. */
export function cellMoments(moments: PlanningMoment[], start: string, end: string) {
  const inCell = moments.filter((m) => m.date >= start && m.date <= end);
  const types = new Set(inCell.map((m) => m.type));
  return { done: types.has("done"), due: types.has("due"), forecast: types.has("forecast"), dates: inCell };
}

/** Afspraak in een cel: of de cel erin valt, en of het de eerste of laatste cel van het blok is. */
export function cellPlan(l: PlanningLineDto, start: string, end: string) {
  const a = l.appointment;
  if (!a || l.closed || a.endOn < start || a.startOn > end) return null;
  return { first: a.startOn >= start, last: a.endOn <= end };
}

/** Nieuwe startdatum na slepen: evenveel dagen verschoven als van de cel waar het blok werd opgepakt. */
export const movedStart = (startOn: string, fromCell: string, toCell: string) =>
  addDays(startOn, daysBetween(fromCell, toCell));

/** Afspraak in woorden: "16 nov 2026 t/m 17 nov 2026, voorlopig". */
export function appointmentText(a: PlanningAppointmentDto) {
  const span = a.days > 1 ? `${planningDay(a.startOn)} t/m ${planningDay(a.endOn)}` : planningDay(a.startOn);
  return `${span}, ${a.confirmed ? "bevestigd" : "voorlopig"}`;
}
